import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, Stack, TextField, Chip, CircularProgress, IconButton, Tooltip, Button, Card, Collapse, Breadcrumbs, Link, Autocomplete } from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, Add as AddIcon, GroupWork as GroupIcon, Search as SearchIcon } from '@mui/icons-material';
import { positionService, skillsService, competencyGroupsService } from '../services/api';
import api from '../services/api';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';

import './PositionManagement.css';
import { useToast } from './Common/ToastProvider';

function useDnD() {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  return { sensors };
}

function DragPreview({ active }) {
  if (!active) return null;
  const data = active.data?.current;
  if (!data) return null;
  if (data.type === 'skill') return (
    <Paper variant="elevation" elevation={6} sx={{ p: 1, bgcolor: 'background.paper' }}>
      <Typography variant="body2">{data.skill?.name}</Typography>
    </Paper>
  );
  if (data.type === 'category') return (
    <Paper variant="elevation" elevation={6} sx={{ p: 1, bgcolor: 'background.paper' }}>
      <Typography variant="caption">Category</Typography>
    </Paper>
  );
  return null;
}

function DraggableSkill({ skill, onClick }) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: `skill-${skill.id}`, data: { type: 'skill', skill } });
  return (
    <Paper ref={setNodeRef} {...attributes} {...listeners} variant="outlined" sx={{ p: 1, cursor: 'grab' }} onClick={onClick}>
      <Typography variant="body2">{skill.name}</Typography>
    </Paper>
  );
}

function DraggableCategoryHeader({ cat, count, children }) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: `cat-${cat.id}`, data: { type: 'category', categoryId: cat.id } });
  return (
    <Stack ref={setNodeRef} {...attributes} {...listeners} direction="row" alignItems="center" spacing={1} sx={{ cursor: 'grab' }}>
      {children}
      <Typography variant="caption" color="text.secondary">{count}</Typography>
    </Stack>
  );
}



function CategorySection({ cat, skills, onSkillClick, open, onToggle }) {
  const colorPalette = ['primary', 'secondary', 'success', 'warning', 'info'];
  const color = colorPalette[cat.idx % colorPalette.length];
  return (
    <Box sx={{ mb: 1.5 }}>
      <DraggableCategoryHeader cat={cat} count={skills.length}>
        <Stack direction="row" alignItems="center" spacing={1} onClick={onToggle}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Chip size="small" label={cat.name} color={color} />
        </Stack>
      </DraggableCategoryHeader>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {skills.map(s => (
            <DraggableSkill key={s.id} skill={s} onClick={() => onSkillClick(s)} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function GroupCard({ group, categoriesById, onRemoveSkill }) {
  const { setNodeRef, isOver } = useDroppable({ id: `group-${group.id}`, data: { type: 'group', groupId: group.id } });
  return (
    <Paper ref={setNodeRef} variant="outlined" sx={{ p: 1.5, minHeight: 220, backgroundColor: isOver ? 'rgba(25,118,210,0.06)' : 'inherit' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>{group.title}</Typography>
        <Chip size="small" label={group.items.length} />
      </Stack>
      {/* Group items by category for display */}
      {[...(() => {
        const byCat = new Map();
        for (const it of group.items) {
          const k = it.categoryId || 'uncat';
          if (!byCat.has(k)) byCat.set(k, []);
          byCat.get(k).push(it);
        }
        return byCat.entries();
      })()].map(([catId, items]) => (
        <Box key={catId} sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Chip size="small" label={categoriesById?.get(catId)?.name || 'Other Skills'} color="primary" />
            <Typography variant="caption" color="text.secondary">{items.length}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            {items.map((it) => (
              <Paper key={it.id} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{it.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{it.categoryName}</Typography>
                  </Box>
                  {onRemoveSkill && (
                    <Button size="small" onClick={() => onRemoveSkill(group.id, it.id)}>Remove</Button>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}
    </Paper>
  );
}


export default function PositionSkillComposer() {
  const toast = useToast();
  const [positions, setPositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [openCats, setOpenCats] = useState({});

  // Restore last selected position on mount
  useEffect(() => {
    const saved = localStorage.getItem('psc:selectedPositionId');
    if (saved) setSelectedPositionId(saved);
  }, []);
  // Persist selection
  useEffect(() => {
    if (selectedPositionId) localStorage.setItem('psc:selectedPositionId', String(selectedPositionId));
  }, [selectedPositionId]);

  // Groups state comes from backend per position
  const [groups, setGroups] = useState([]);

  const [activeGroupId, setActiveGroupId] = useState('');


  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pos, cats, sk] = await Promise.all([
          positionService.getAll(),
          api.get('/skill-categories?activeOnly=true'),
          skillsService.getAll(),
        ]);
        setPositions(pos || []);
        setCategories(cats || []);
        setSkills(sk || []);
        // Auto-select first position if none selected/restored
        if ((!selectedPositionId || String(selectedPositionId).length === 0) && Array.isArray(pos) && pos.length > 0) {
          setSelectedPositionId(pos[0].id);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load groups for selected position and restore last active group (per-position)
  useEffect(() => {
    if (!selectedPositionId) return;
    const run = async () => {
      try {
        const list = await competencyGroupsService.getByPosition(selectedPositionId);
        if (Array.isArray(list)) {
          // Replace groups with server-provided list (reset items; details load separately)
          setGroups(list.map(x => ({ id: String(x.id), title: x.title || String(x.id), items: [] })));
          const savedAg = localStorage.getItem(`psc:${selectedPositionId}:activeGroupId`);
          const ids = new Set(list.map(x => String(x.id)));
          if (savedAg && ids.has(String(savedAg))) {
            setActiveGroupId(String(savedAg));
          } else {
            setActiveGroupId(list.length > 0 ? String(list[0].id) : '');
          }
        } else {
          setGroups([]);
          setActiveGroupId('');
        }
      } catch (e) {
        console.error('Failed to load groups for position', e);
        setGroups([]);
        setActiveGroupId('');
      }
    };
    run();
  }, [selectedPositionId]);

  // Persist last active group per position
  useEffect(() => {
    if (selectedPositionId && activeGroupId) {
      localStorage.setItem(`psc:${selectedPositionId}:activeGroupId`, String(activeGroupId));
    }
  }, [selectedPositionId, activeGroupId]);

  useEffect(() => {
    const loadActiveDetails = async () => {
      if (!selectedPositionId || !activeGroupId) return;
      try {
        const details = await api.get(`/positions/${selectedPositionId}/competency-groups/${activeGroupId}`);
        if (details && Array.isArray(details.categories)) {
          // Build items with full names immediately using skillsById map; fallback to placeholder if truly missing
          const items = [];
          for (const c of details.categories) {
            for (const s of (c.skills || [])) {
              const known = skillsById.get(s.skillId);
              if (known) items.push(known);
              else items.push({ id: s.skillId, name: `#${s.skillId}`, categoryId: c.categoryId, categoryName: categoriesById.get(c.categoryId)?.name || '' });
            }
          }
          setGroups(prev => prev.map(g => g.id === activeGroupId ? { ...g, title: details.title || g.title, items } : g));
        }
      } catch (e) {
        console.error('Failed to load group details', e);
      }
    };
    loadActiveDetails();
  }, [selectedPositionId, activeGroupId, skills]);


  const filteredSkills = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (skills || [])
      .filter(s => !q || (s.name || '').toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q))
      .map(s => ({ id: s.id, name: s.name, categoryId: s.skillCategoryId, categoryName: s.skillCategoryName }));
  }, [skills, search]);

  const addToGroup = (groupId, skill) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      if (g.items.some(x => x.id === skill.id)) return g; // prevent duplicate in the same group
      return { ...g, items: [...g.items, skill] };
    }));
  };

  // Fast lookup map: ALL skills by id (not filtered by search)
  const skillsById = useMemo(() => {
    const m = new Map();
    for (const s of skills || []) {
      m.set(s.id, { id: s.id, name: s.name, categoryId: s.skillCategoryId, categoryName: s.skillCategoryName });
    }
    return m;
  }, [skills]);

  const removeFromGroup = (groupId, skillId) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, items: g.items.filter(x => x.id !== skillId) } : g));
  };

  const { sensors } = useDnD();
  const [activeDrag, setActiveDrag] = useState(null);

  const categoriesById = useMemo(() => {
    const map = new Map();
    for (const c of categories || []) map.set(c.id, c);
    return map;
  }, [categories]);

  // Helper to fetch and load a group's details directly
  const fetchGroupDetails = async (groupId) => {
    if (!selectedPositionId || !groupId) return;
    try {
      const details = await api.get(`/positions/${selectedPositionId}/competency-groups/${groupId}`);
      console.debug('[PSC] loaded group details', { positionId: selectedPositionId, groupId, categories: details?.categories?.length || 0 });
      if (details && Array.isArray(details.categories)) {
        const items = [];
        for (const c of details.categories) {
          for (const s of (c.skills || [])) {
            const known = skillsById.get(s.skillId);
            if (known) items.push(known);
            else items.push({ id: s.skillId, name: `#${s.skillId}`, categoryId: c.categoryId, categoryName: categoriesById.get(c.categoryId)?.name || '' });
          }
        }
        setGroups(prev => prev.map(g => g.id === groupId ? { ...g, title: details.title || g.title, items } : g));
      }
    } catch (e) {
      console.error('Failed to load group details (click)', e);
    }
  };

  const handleDragStart = (event) => {
    setActiveDrag(event.active?.data?.current || null);
  };
  const handleDragCancel = () => setActiveDrag(null);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!active || !over) return;
    const a = active.data?.current;
    const o = over.data?.current;
    if (!a || !o) return;

    // Debug logs to help verify DnD events at runtime
    try { console.debug('[PSC] dragEnd', { active: a?.type, over: o?.type, groupId: o?.groupId, activeGroupId }); } catch {}

    // Only accept drops onto the active group canvas
    if (o.type === 'group' && activeGroupId && o.groupId === activeGroupId) {
      if (a.type === 'skill') {
        const skill = a.skill;
        setGroups(prev => prev.map(g => {
          if (g.id !== activeGroupId) return g;
          if (g.items.some(x => x.id === skill.id)) return g; // prevent duplicate in same group
          return { ...g, items: [...g.items, skill] };
        }));
      }
      if (a.type === 'category') {
        const catId = a.categoryId;
        const toAdd = filteredSkills.filter(s => s.categoryId === catId);
        setGroups(prev => prev.map(g => {
          if (g.id !== activeGroupId) return g;
          const existingIds = new Set(g.items.map(x => x.id));
          const merged = [...g.items];
          for (const s of toAdd) if (!existingIds.has(s.id)) merged.push(s);
          return { ...g, items: merged };
        }));
      }
    }
  };

  const [snack, setSnack] = useState({ open: false, message: '', error: false });

  const buildPayloadFromActive = () => {
    const grp = groups.find(g => g.id === activeGroupId);
    if (!grp) return null;
    // group items by category with stable order as appears in state
    const byCat = new Map();
    for (const s of grp.items) {
      const list = byCat.get(s.categoryId) || [];
      list.push({ skillId: s.id });
      byCat.set(s.categoryId, list);
    }
    const categories = [...byCat.entries()].map(([categoryId, skills], idx) => ({
      categoryId,
      sortOrder: idx + 1,
      skills: skills.map((x, i) => ({ ...x, sortOrder: i + 1 }))
    }));
    return { title: grp.title, categories };
  };

  const handleSave = async () => {
    if (!activeGroupId) return;
    if (!selectedPositionId) {
      setSnack({ open: true, message: 'Select a position first', error: true });
      return;
    }
    const payload = buildPayloadFromActive();
    if (!payload) return;
    try {
      await competencyGroupsService.upsert(activeGroupId, payload, selectedPositionId);
      // Refresh list (titles) and active group details from server to ensure persistence
      const [list, details] = await Promise.all([
        competencyGroupsService.getByPosition(selectedPositionId),
        api.get(`/positions/${selectedPositionId}/competency-groups/${activeGroupId}`)
      ]);
      if (Array.isArray(list) && list.length > 0) {
        setGroups(prev => prev.map(g => {
          const found = list.find(x => String(x.id) === String(g.id));
          return found ? { ...g, title: found.title || g.title } : g;
        }));
      }
      if (details && Array.isArray(details.categories)) {
        const items = [];
        for (const c of details.categories) {
          for (const s of (c.skills || [])) {
            const known = skillsById.get(s.skillId);
            if (known) items.push(known);
            else items.push({ id: s.skillId, name: `#${s.skillId}`, categoryId: c.categoryId, categoryName: categoriesById.get(c.categoryId)?.name || '' });
          }
        }
        setGroups(prev => prev.map(g => g.id === activeGroupId ? { ...g, items } : g));
      }
      setSnack({ open: true, message: 'Saved', error: false });
    } catch (e) {
      console.error('Save failed', e);
      setSnack({ open: true, message: 'Save failed', error: true });
    }
  };  // Create a new competency group for the selected position
  const handleAddGroup = async () => {
    if (!selectedPositionId) return;
    const code = window.prompt('Enter new group code (e.g., DBA):');
    if (!code) return;
    const title = window.prompt('Enter group title:', code) || code;
    try {
      await competencyGroupsService.create(selectedPositionId, { code: code.trim(), title: title.trim(), sortOrder: (groups?.length || 0) + 1 });
      const list = await competencyGroupsService.getByPosition(selectedPositionId);
      if (Array.isArray(list)) {
        setGroups(list.map(x => ({ id: String(x.id), title: x.title || String(x.id), items: [] })));
        setActiveGroupId(String(code.trim()));
        await fetchGroupDetails(String(code.trim()));
      }
    } catch (e) {
      console.error('Failed to create group', e);
      toast('Failed to create group', { variant: 'error' });
    }
  };

  return (
    <Box className="position-management-container">
      {/* Header with Breadcrumbs */}
      <Box className="position-header">
        <Breadcrumbs className="breadcrumbs">
          <Link color="inherit" onClick={(e) => { e.preventDefault(); window.history.back(); }} className="breadcrumb-link">Settings</Link>
          <Typography color="text.primary">Position Skill Composer</Typography>
        </Breadcrumbs>
        <Box className="header-content">
          <Box className="title-section">
            <GroupIcon className="header-icon" />
            <Box>
              <Typography variant="h5" className="page-title">Position Skill Composer</Typography>
              <Typography variant="body2" className="page-subtitle">Compose competency groups per position</Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              options={positions}
              getOptionLabel={(o) => o?.title || ''}
              value={positions.find(p => p.id === selectedPositionId) || null}
              onChange={(e, v) => setSelectedPositionId(v?.id || '')}
              renderInput={(params) => <TextField {...params} label="Position" size="small" />}
              sx={{ minWidth: 320 }}
            />
            <TextField size="small" placeholder="Search skills" InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button className="add-button" variant="contained" startIcon={<AddIcon />}>Add Competency Group</Button>
          </Stack>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}><CircularProgress /></Box>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragCancel={handleDragCancel} onDragEnd={handleDragEnd}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            {/* Left: Skill pool (collapsible categories) */}
            <Box sx={{ width: { xs: '100%', lg: 360 } }}>
              <Card className="content-card">
                <Box sx={{ p: 1, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Skill Pool</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => setOpenCats(Object.fromEntries((categories||[]).map(c => [c.id, false]))) }>Collapse All</Button>
                    <Button size="small" onClick={() => setOpenCats(Object.fromEntries((categories||[]).map(c => [c.id, true]))) }>Expand All</Button>
                  </Stack>
                </Box>
                <Box sx={{ p: 1, maxHeight: 540, overflow: 'auto' }}>
                  {(categories || []).map((cat, idx) => {
                    const open = openCats[cat.id] ?? true;
                    const toggle = () => setOpenCats(prev => ({ ...prev, [cat.id]: !(prev[cat.id] ?? true) }));
                    const catSkills = filteredSkills.filter(s => s.categoryId === cat.id);
                    return (
                      <CategorySection key={cat.id} cat={{ ...cat, idx }} skills={catSkills} open={open} onToggle={toggle} onSkillClick={() => {}} />
                    );
                  })}
                </Box>
              </Card>
            </Box>

            {/* Center: Canvas shows ONLY the selected active group */}
            <Box sx={{ flex: 1 }}>
              <Card className="content-card">
                <Box sx={{ p: 1, pb: 0 }}>
                  <Typography variant="subtitle2">Canvas</Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                  {activeGroupId ? (
                    <GroupCard group={groups.find(g => g.id === activeGroupId) || { id: activeGroupId, title: activeGroupId, items: [] }} categoriesById={categoriesById} onRemoveSkill={(gid, sid) => removeFromGroup(gid, sid)} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">Select a competency group from the right to start editing</Typography>
                  )}
                </Box>
              </Card>
            </Box>

            {/* Right: Competency Groups list with Save */}
            <Box sx={{ width: { xs: '100%', lg: 320 } }}>
              <Card className="content-card">
                <Box sx={{ p: 1, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Competency Groups</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddGroup}>Add</Button>
                    <Button size="small" variant="contained" onClick={handleSave} disabled={!activeGroupId}>Save</Button>
                  </Stack>
                </Box>
                <Box sx={{ p: 1 }}>
                  <Stack spacing={1}>
                    {groups.map(g => (
                      <Paper key={g.id} variant="outlined" sx={{ p: 1, borderColor: g.id === activeGroupId ? 'primary.main' : undefined, cursor: 'pointer' }} onClick={() => { setActiveGroupId(g.id); fetchGroupDetails(g.id); }}>
                        <Typography variant="body2" fontWeight={600}>{g.title}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              </Card>
            </Box>
          </Stack>
          <DragOverlay dropAnimation={null}>{activeDrag?.type === 'skill' ? (
            <Paper variant="elevation" elevation={6} sx={{ p: 1, bgcolor: 'background.paper' }}>
              <Typography variant="body2">{activeDrag?.skill?.name}</Typography>
            </Paper>
          ) : activeDrag?.type === 'category' ? (
            <Paper variant="elevation" elevation={6} sx={{ p: 1, bgcolor: 'background.paper' }}>
              <Typography variant="caption">Category</Typography>
            </Paper>
          ) : null}
            {/* We'll let the overlay render based on the active drag item */}
          </DragOverlay>
        </DndContext>

      )}
    </Box>
  );
}



